import React from "react";
import { useTranslation } from 'react-i18next';
import { getTestsCount } from '../utils/testUtils';
import Spinner from "./ui/spinner";
import LastAnalysisBanner from "./LastAnalysisBanner";
import './NodesList.css';

const NodesList = ({
  nodes,
  impactRadarResult,
  selectedNode,
  selectNode,
  toggleNodeSelection,
  deleteNode,
  loading,
  loadingNode,
  relationships,
  onEditNode,
  onViewDetails,
  onShowTests,
  onShowRelations,
  onOpenImpactRadar, // Nouveau prop pour ouvrir la modal Impact Radar
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = React.useState('');
  
  return (
    <div className="nodes-list-container">
      {/* Banneau de la dernière analyse Impact Radar */}
      <LastAnalysisBanner onOpenAnalysis={onOpenImpactRadar} />

      {/* Champ de recherche - Dessous de la banner */}
      <input
        type="text"
        placeholder={t('nodes.searchPlaceholder') || "Search for a node by name..."}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="searchNodeInput"
      />

      {/* Grille des nœuds */}
      <div className="nodes-grid">
        {nodes.filter(node => 
          node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (node.description && node.description.toLowerCase().includes(searchTerm.toLowerCase()))
        ).map((node, index) => {
      const impactNode = impactRadarResult?.impactedNodes?.find(
        (n) => n.name === node.name
      );
      const typeBadgeClass =
        node.type === "Feature"
          ? "type-badge-feature"
          : node.type === "API"
          ? "type-badge-api"
          : node.type === "Database"
          ? "type-badge-database"
          : node.type === "Module"
          ? "type-badge-module"
          : "type-badge-default";

      return (
        <div key={index} className="node-card">
          <div className="node-card-header">
            <div className="node-title-wrap">
              <span className={`node-type-badge ${typeBadgeClass}`}>
                {node.type}
              </span>
              <h4 className="node-title">{node.name}</h4>
            </div>
            {impactNode && (
              <span
                className={
                  "impact-badge " +
                  (impactNode.impactType === "core-feature"
                    ? "impact-critical"
                    : impactNode.impactType === "test-coverage-missing"
                    ? "impact-warning"
                    : impactNode.impactType === "test-obsolete"
                    ? "impact-caution"
                    : "impact-info")
                }
                title={impactNode.impactType}
              >
                {impactNode.impactType}
              </span>
            )}
          </div>

          <p className="node-description">{node.description}</p>

          <div className="node-tech-info">
            {node.technology && (
              <span className="tech-tag">
                <span className="tech-icon">⚙️</span>
                {node.technology}
              </span>
            )}
            {node.status && (
              <span className="status-tag">
                {node.status === "Terminé"
                  ? "✅"
                  : node.status === "En développement"
                  ? "🚧"
                  : node.status === "En test"
                  ? "🧪"
                  : "📝"}
                {node.status}
              </span>
            )}
          </div>

          <div className="node-details-row">
            <div className="node-metadata">
              {getTestsCount(node.testsJson) > 0 ? (
                <div
                  className="tests-badge"
                  onClick={() => {
                    selectNode(node);
                    if (onShowTests) {
                      onShowTests(node);
                    } else {
                      // Fallback ancien comportement
                      setTimeout(() => {
                        const targetElement = document.getElementById("section-tests") || 
                                             document.getElementById("section-tests-global");
                        if (targetElement) {
                          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }, 100);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                  title="Cliquez pour voir les tests"
                >
                  <span className="tests-icon">🧪</span>
                  <span className="tests-count">{getTestsCount(node.testsJson)}</span>
                  <span className="tests-label">tests</span>
                </div>
              ) : (
                <div className="no-tests-badge">
                  <span className="tests-icon">⚠️</span>
                  <span className="tests-label">Sans tests</span>
                </div>
              )}

              <span
                className="relations-badge"
                onClick={() => {
                  selectNode(node);
                  if (onShowRelations) {
                    onShowRelations(node);
                  } else {
                    // Fallback ancien comportement
                    setTimeout(() => {
                      const targetElement = document.getElementById("section-relations-list") ||
                                           document.querySelector('[id*="relations"]');
                      if (targetElement) {
                        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }, 100);
                  }
                }}
                style={{ cursor: "pointer" }}
                title="Cliquez pour voir les relations"
              >
                <span className="relations-icon">🔗</span>
                <span className="relations-count">
                  {
                    relationships.filter(
                      (rel) =>
                        rel.nodeName1 === node.name ||
                        rel.nodeName2 === node.name
                    ).length
                  }
                </span>
                <span className="relations-label">relations</span>
              </span>
            </div>

            <div
              className="view-details-btn"
              onClick={() => {
                if (onViewDetails) {
                  onViewDetails(node);
                } else {
                  // Fallback ancien comportement
                  selectNode(node);
                  const targetElement = document.getElementById("section-create-node");
                  if (targetElement) {
                    targetElement.scrollIntoView({ behavior: "smooth" });
                  } else {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }
              }}
            >
              {t('nodesList.viewDetails')}
              <span className="details-icon">→</span>
            </div>
          </div>

          <div className="node-card-actions">
            <button
              className="btn-edit-node"
              onClick={() => {
                if (onEditNode) {
                  onEditNode(node);
                } else {
                  // Fallback ancien comportement
                  selectNode(node);
                  const targetElement = document.getElementById("section-create-node");
                  if (targetElement) {
                    targetElement.scrollIntoView({ behavior: "smooth" });
                  } else {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }
              }}
              title={`${t('nodesList.editNode')} "${node.name}"`}
            >
              <span className="btn-icon">✏️</span>
              <span className="btn-text">{t('nodesList.editNode')}</span>
            </button>

            <button
              className={`btn-select-node ${
                selectedNode?.name === node.name ? "btn-selected" : ""
              }`}
              onClick={() => toggleNodeSelection(node)}
              title={
                selectedNode?.name === node.name
                  ? t('nodesList.deselectNode')
                  : t('nodesList.selectNodeAs', { name: node.name })
              }
            >
              {selectedNode?.name === node.name
                ? t('nodesList.selected')
                : t('nodesList.select')}
            </button>

            <button
              onClick={() => deleteNode(node.name)}
              disabled={loading}
              className="btn-delete-node"
              title={`Supprimer le nœud "${node.name}" et toutes ses relations`}
            >
              {loadingNode === node.name ? <Spinner /> : "🗑️"}
            </button>
          </div>
        </div>
      );
    })}
      </div>
    </div>
  );
};

export default NodesList;